import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertTriangle, Loader2 } from "lucide-react";
import { legal } from "@/lib/api";

/**
 * Renders the privacy notice or the terms.
 *
 * The documents come from the server rather than living in the bundle, so the
 * version the user reads is always the version their acceptance is recorded
 * against.
 */

/** Minimal emphasis rendering: the notice uses **bold** for the data categories. */
function Paragraph({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p className="text-[13.5px] leading-relaxed text-ink-muted">
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i} className="font-medium text-ink">{part.slice(2, -2)}</strong>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </p>
  );
}

export default function LegalDialog({ open, onOpenChange, document: kind = "privacy" }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const fetcher = kind === "terms" ? legal.terms : legal.privacy;
    fetcher()
      .then(setDoc)
      .catch(() => setDoc(null))
      .finally(() => setLoading(false));
  }, [open, kind]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px]" data-testid={`legal-${kind}`}>
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl">
            {doc?.title || (kind === "terms" ? "Condizioni d'uso" : "Informativa privacy")}
          </DialogTitle>
          {doc?.version && (
            <DialogDescription className="numa-meta">
              Versione del {doc.version}
            </DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-[13.5px] text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
          </div>
        ) : !doc ? (
          <p className="py-6 text-[13.5px] text-ink-muted">Documento non disponibile.</p>
        ) : (
          <ScrollArea className="max-h-[62vh] pr-4">
            {doc.configured === false && (
              <div className="mb-4 flex gap-2.5 rounded-md border border-warn/30 bg-warn/5 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn" />
                <div className="text-[12.5px] leading-relaxed">
                  <p className="font-medium text-ink">Documento non ancora completo</p>
                  <p className="mt-0.5 text-ink-muted">
                    Alcuni dati del titolare del trattamento non sono ancora pubblicati.
                    Per esercitare i tuoi diritti o chiedere informazioni puoi intanto
                    scrivere all&apos;indirizzo di assistenza indicato in fondo a questo
                    documento.
                  </p>
                </div>
              </div>
            )}

            {doc.intro && <Paragraph text={doc.intro} />}

            <div className="mt-5 space-y-5">
              {(doc.sections || []).map((section) => (
                <section key={section.heading}>
                  <h3 className="font-serif-display text-[15px] font-semibold text-ink">
                    {section.heading}
                  </h3>
                  <div className="mt-1.5 space-y-2">
                    {section.body.map((line, i) => (
                      <Paragraph key={i} text={line} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
